import { useMutation, useQuery, useQueryClient, useTRPC } from "@luckymenu/api-client/react";
import type {
	AcceptBatchInput,
	LiveBoard,
	RejectBatchInput,
	ServiceRequestStatus,
} from "@luckymenu/core";
import { useMutationState } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
	boardView,
	EMPTY_BOARD,
	type PendingChanges,
	withoutBatch,
	withPending,
	withRequest,
} from "../ordering/board";
import { type OrderingProblem, orderingProblemOf } from "../ordering/problems";
import { useStaffCore } from "../provider";
import { useCan } from "./use-navigation";

/**
 * Decisions and request changes on their way to the server, from the
 * mutation cache, so every screen (the board, the alert, the Tables tab)
 * shows them at once, whichever component sent them. `useMutationState`
 * shares structure, so the list keeps its identity while nothing changes.
 */
export function usePendingChanges(): PendingChanges {
	const trpc = useTRPC();
	const decisions = useMutationState({
		filters: { mutationKey: trpc.staff.ordering.pathKey(), status: "pending" },
		select: (m) => ({
			key: m.options.mutationKey,
			variables: m.state.variables as { batchId?: string; id?: string } | undefined,
		}),
	});
	const accept = JSON.stringify(trpc.staff.ordering.acceptBatch.mutationKey());
	const reject = JSON.stringify(trpc.staff.ordering.rejectBatch.mutationKey());
	const ack = JSON.stringify(trpc.staff.ordering.ackServiceRequest.mutationKey());
	const resolve = JSON.stringify(trpc.staff.ordering.resolveServiceRequest.mutationKey());
	return useMemo(() => {
		const batchIds = new Set<string>();
		const requests = new Map<string, ServiceRequestStatus>();
		for (const d of decisions) {
			const key = JSON.stringify(d.key);
			if ((key === accept || key === reject) && d.variables?.batchId)
				batchIds.add(d.variables.batchId);
			if (key === ack && d.variables?.id && !requests.has(d.variables.id))
				requests.set(d.variables.id, "acknowledged");
			if (key === resolve && d.variables?.id) requests.set(d.variables.id, "resolved");
		}
		return { batchIds, requests };
	}, [decisions, accept, reject, ack, resolve]);
}

/**
 * The live board from `staff.ordering.listLive` with the changes on their
 * way applied. Kept fresh by the socket (each event refetches it) and polled
 * while offline. Needs `order.view`.
 */
export function useLiveBoard() {
	const trpc = useTRPC();
	const canView = useCan("order.view");
	const query = useQuery({ ...trpc.staff.ordering.listLive.queryOptions(), enabled: canView });
	const pending = usePendingChanges();
	const board = useMemo(
		() => withPending(query.data ?? EMPTY_BOARD, pending),
		[query.data, pending],
	);
	const view = useMemo(() => boardView(board), [board]);
	return {
		status: query.data
			? ("ready" as const)
			: query.isError
				? ("error" as const)
				: ("loading" as const),
		error: query.error,
		retry: () => void query.refetch(),
		board,
		view,
	};
}

/** Acting on an order or a call is the moment to offer push notifications (push/offer.ts). */
function useEngage() {
	const core = useStaffCore();
	return useCallback(() => core.pushPrompt.getState().engage(), [core]);
}

/** Writes to the cached board once the server has answered. */
function useBoardCache() {
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	return useMemo(() => {
		const queryKey = trpc.staff.ordering.listLive.queryKey();
		return {
			update: (change: (board: LiveBoard) => LiveBoard) =>
				queryClient.setQueryData<LiveBoard>(queryKey, (old) => (old ? change(old) : old)),
			/** Refetches every ordering query: the board, and later the order screens. */
			refresh: () => queryClient.invalidateQueries({ queryKey: trpc.staff.ordering.pathKey() }),
		};
	}, [trpc, queryClient]);
}

export type BatchProblem = {
	batchId: string;
	action: "accept" | "reject" | "block";
	problem: OrderingProblem;
};

/**
 * Accept and Reject on a QR batch's card (OP-05). The card leaves the board
 * as soon as it is tapped; if the server refuses, it comes back and
 * `problem` says why. Accepting may take items out (`removeItemIds`), such
 * as a dish that just sold out. "Reject and block" rejects, then blocks the
 * diner's session so it cannot order again. Each call is safe to repeat: the
 * server returns a batch already decided the same way.
 */
export function useBatchDecisions() {
	const trpc = useTRPC();
	const cache = useBoardCache();
	const engage = useEngage();
	const [problem, setProblem] = useState<BatchProblem | null>(null);
	const acceptMutation = useMutation(
		trpc.staff.ordering.acceptBatch.mutationOptions({
			onSuccess: (result) => {
				engage();
				cache.update((b) => withoutBatch(b, result.batch.id));
			},
			onSettled: () => void cache.refresh(),
		}),
	);
	const rejectMutation = useMutation(
		trpc.staff.ordering.rejectBatch.mutationOptions({
			onSuccess: (batch) => {
				engage();
				cache.update((b) => withoutBatch(b, batch.id));
			},
			onSettled: () => void cache.refresh(),
		}),
	);
	const blockMutation = useMutation(trpc.staff.ordering.blockGuest.mutationOptions());

	const accept = useCallback(
		async (input: AcceptBatchInput) => {
			setProblem(null);
			try {
				return await acceptMutation.mutateAsync(input);
			} catch (error) {
				setProblem({ batchId: input.batchId, action: "accept", problem: orderingProblemOf(error) });
				return null;
			}
		},
		[acceptMutation],
	);

	const reject = useCallback(
		async (input: RejectBatchInput, options: { block?: boolean } = {}) => {
			setProblem(null);
			try {
				await rejectMutation.mutateAsync(input);
			} catch (error) {
				setProblem({ batchId: input.batchId, action: "reject", problem: orderingProblemOf(error) });
				return false;
			}
			if (!options.block) return true;
			try {
				await blockMutation.mutateAsync({ batchId: input.batchId, blocked: true });
				return true;
			} catch (error) {
				setProblem({ batchId: input.batchId, action: "block", problem: orderingProblemOf(error) });
				return false;
			}
		},
		[rejectMutation, blockMutation],
	);

	return { accept, reject, problem, clearProblem: () => setProblem(null) };
}

export type RequestProblem = {
	requestId: string;
	action: "acknowledge" | "resolve";
	problem: OrderingProblem;
};

/**
 * "On my way" (acknowledge: stops the alert, the diner sees it) and "Done"
 * (resolve: off the board) for service requests. Optimistic like the batch
 * decisions; repeating either is harmless.
 */
export function useServiceRequestActions() {
	const trpc = useTRPC();
	const cache = useBoardCache();
	const engage = useEngage();
	const [problem, setProblem] = useState<RequestProblem | null>(null);
	const options = {
		onSuccess: (request: Parameters<typeof withRequest>[1]) => {
			engage();
			cache.update((b) => withRequest(b, request));
		},
		onSettled: () => void cache.refresh(),
	};
	const ackMutation = useMutation(trpc.staff.ordering.ackServiceRequest.mutationOptions(options));
	const resolveMutation = useMutation(
		trpc.staff.ordering.resolveServiceRequest.mutationOptions(options),
	);
	const run = useCallback(
		async (action: RequestProblem["action"], id: string) => {
			setProblem(null);
			try {
				await (action === "acknowledge" ? ackMutation : resolveMutation).mutateAsync({ id });
				return true;
			} catch (error) {
				setProblem({ requestId: id, action, problem: orderingProblemOf(error) });
				return false;
			}
		},
		[ackMutation, resolveMutation],
	);
	return {
		acknowledge: (id: string) => run("acknowledge", id),
		resolve: (id: string) => run("resolve", id),
		problem,
		clearProblem: () => setProblem(null),
	};
}

/** The time now, updated every `intervalMs`, for "5 min ago" and time seated. */
export function useNow(intervalMs = 30_000): number {
	const [now, setNow] = useState(() => Date.now());
	useEffect(() => {
		const timer = setInterval(() => setNow(Date.now()), intervalMs);
		return () => clearInterval(timer);
	}, [intervalMs]);
	return now;
}
